import { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Grid,
  Button,
  Switch,
  Table,
  TableContainer,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  TablePagination,
  TableFooter,
  Snackbar,
  Alert,
  Typography,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import { Close, Delete, Edit } from "@mui/icons-material";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import PropTypes from "prop-types";
import axios from "../../config/axios";
import { getUserData, hasAccess } from "../../utils/utilities";
import AddAdminModal from "./addAdminModal";
import EditModal from "./editUserModal";
import SearchBar from "../../components/SearchBar";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";

const initAlert = { status: false, msg: "", variant: "success" };

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const UserList = () => {
  const [userList, setUserList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  // search data
  const [search, setSearch] = useState("");
  // pagination data
  const [totalRows, setTotalRows] = useState(5);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");
  // alert data
  const [alert, setAlert] = useState(initAlert);
  // modals
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] =
    useState(false);
  const [editUserModal, setEditUserModal] = useState(false);
  const [userData, setUserData] = useState({});
  // misc
  const [deleteProfile, setDeleteProfile] = useState({});

  const userRoles = {
    SuperAdmin: "Super Admin",
    Company: "Company",
    CompanyAdmin: "Company Admin",
    Admin: "Employee",
  };

  const closeAlertMessage = () => {
    setAlert(initAlert);
  };

  const handleChangePage = (e, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationDialog(false);
  };

  // API Services
  const getUserList = (
    searchValue = search,
    recordsPerPage = rowsPerPage,
    currentPage = page
  ) => {
    const query = {
      pagination: true,
      search: searchValue,
      rowsPerPage: recordsPerPage,
      page: currentPage + 1,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
    };

    const body = { adminId: getUserData().id };

    axios
      .post("/user/user-list", body, { params: query })
      .then((res) => {
        if (res.status === 200) {
          setTotalRows(res.data.data?.total || 0);
          setUserList(res.data.data?.list || []);
        }
      })
      .catch((err) => console.log(err));
  };

  const getAdminList = (
    searchValue = search,
    recordsPerPage = rowsPerPage,
    currentPage = page
  ) => {
    const role = getUserData().role;

    const query = {
      pagination: true,
      search: searchValue,
      rowsPerPage: recordsPerPage,
      page: currentPage + 1,
      sortBy: orderBy,
      descending: order === "asc" ? false : true,
      company_id:
        role == "Company" || role == "CompanyAdmin"
          ? getUserData().company_id
          : "",
    };

    axios
      .get("/admin/admin-list", { params: query })
      .then((res) => {
        if (res.status === 200) {
          setTotalRows(res.data.data?.total || 0);
          setUserList(res.data.data?.list || []);
        }
      })
      .catch((err) => console.log(err));
  };

  const getFunction = () => {
    const role = getUserData().role;

    if (["SuperAdmin", "Company", "CompanyAdmin"].includes(role)) {
      return getAdminList;
    } else if (role === "Admin") {
      return getUserList;
    }
    return null;
  };

  const toggleUserStatus = (userId) => {
    const role = getUserData().role;

    let url, getFun, body;

    if (["SuperAdmin", "Company", "CompanyAdmin"].includes(role)) {
      url = "admin/toggle-status";
      getFun = getAdminList;
      body = { adminId: userId };
    } else if (role === "Admin") {
      url = "user/toggle-status";
      getFun = getUserList;
      body = { userId };
    }

    axios
      .patch(url, body)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            getFun(search, rowsPerPage, page);
            setAlert({
              status: true,
              msg: res.data.message,
              variant: "success",
            });
          } else {
            if (res.data.type === "error") {
              setAlert({
                status: true,
                msg: res.data.error?.message || "",
                variant: "error",
              });
            }
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleDeleteAdmin = (id) => {
    axios
      .delete(`admin/delete?id=${id}`)
      .then((res) => {
        if (res.status === 200) {
          if (res.data.status) {
            setAlert({
              status: true,
              msg: res.data.message,
              variant: "success",
            });
            setDeleteProfile({});
            getFunction()(search, rowsPerPage, page);
          } else {
            if (res.data.type === "error") {
              setAlert({
                status: true,
                msg: res.data.error?.message || "",
                variant: "error",
              });
            }
          }
        }
      })
      .catch((err) => console.log({ err }));
  };

  useEffect(() => {
    getFunction()(search, rowsPerPage, page);
    // if (["SuperAdmin", "Company", "CompanyAdmin"].includes(role)) {
    //   getList(search, rowsPerPage, page);
    // } else if (role === "Admin") {
    //   getUserList(search, rowsPerPage, page);
    // }
  }, [rowsPerPage, page, order, orderBy]);

  const fetchCompanyOptions = () => {
    axios
      .get("/admin/all-companies")
      .then((res) => {
        if (res.status === 200) {
          setCompanyList(
            res.data.data.map((cmp) => ({ name: cmp.name, id: cmp.id }))
          );
        }
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    fetchCompanyOptions();
  }, []);

  const createSortHandler = (property) => (event) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: "Name",
      sortable: true,
    },
    {
      id: "email",
      numeric: false,
      disablePadding: true,
      label: "Email",
      sortable: true,
    },
    {
      id: "company",
      numeric: false,
      disablePadding: true,
      label: "Company",
      sortable: false,
    },
    {
      id: "role",
      numeric: false,
      disablePadding: true,
      label: "Role",
      sortable: false,
    },
    {
      id: "action",
      numeric: false,
      disablePadding: true,
      label: "Actions",
      sortable: false,
    },
  ];

  return (
    <div>
      <Grid container rowSpacing={0}>
        <Grid xs={12} item sx={{ pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: "20px" }} color="#474747">
            User Management
          </Typography>
        </Grid>
        <Grid item xs={12} px={"25px"} sx={{ backgroundColor: "white", py: 3 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems="stretch"
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 2 }}>
              <SearchBar
                sx={{ width: "100%" }}
                onSearch={(search) => {
                  setSearch(search);
                  getFunction()(search, rowsPerPage, page);
                }}
              />
            </Box>
            {hasAccess() ? (
              <Button
                variant="contained"
                sx={{ textTransform: "capitalize" }}
                onClick={() => setAddAdminModal(true)}
              >
                Add New User
              </Button>
            ) : null}
          </Stack>
          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="simple table" className={"custom-table br-0"}>
              <TableHead sx={{ backgroundColor: "#f4f6f9" }}>
                <TableRow>
                  <TableCell
                    key="index"
                    sx={{
                      width: "80px",
                      minWidth: "80px",
                      textAlign: "center",
                    }}
                  >
                    No.
                  </TableCell>
                  {headCells.map((headCell) => (
                    <TableCell
                      size="small"
                      key={headCell.id}
                      align={headCell.numeric ? "right" : "left"}
                      sortDirection={orderBy === headCell.id ? order : false}
                    >
                      {headCell.sortable ? (
                        <TableSortLabel
                          active={orderBy === headCell.id}
                          direction={orderBy === headCell.id ? order : "asc"}
                          onClick={createSortHandler(headCell.id)}
                        >
                          {headCell.label}
                          {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                              {order === "desc"
                                ? "sorted descending"
                                : "sorted ascending"}
                            </Box>
                          ) : null}
                        </TableSortLabel>
                      ) : (
                        headCell.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!userList.length ? (
                  <TableRow>
                    <TableCell size="small" colSpan={4}>
                      <Typography sx={{ width: "100%", textAlign: "center" }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  userList.map((user, ind) => (
                    <TableRow key={ind}>
                      <TableCell sx={{ textAlign: "center" }}>
                        {page * rowsPerPage + (ind + 1)}
                      </TableCell>
                      <TableCell size="small">{user.name}</TableCell>
                      <TableCell size="small">{user.email}</TableCell>
                      <TableCell size="small">{user?.companyName}</TableCell>
                      <TableCell size="small">{userRoles[user.role]}</TableCell>
                      <TableCell size="small">
                        <Box style={{ display: "flex" }}>
                          <Switch
                            color="primary"
                            checked={!user.is_disabled}
                            onChange={() => toggleUserStatus(user._id)}
                          />
                          <Tooltip title="Delete">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setDeleteConfirmationDialog(true);
                                setDeleteProfile(user);
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              color="inherit"
                              sx={{ padding: 0, marginLeft: "5px" }}
                              onClick={() => {
                                setEditUserModal(true);
                                setUserData(user);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      5,
                      10,
                      25,
                      { label: "All", value: -1 },
                    ]}
                    count={totalRows}
                    colSpan={6}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={alert.status}
        onClose={closeAlertMessage}
        message={alert.msg}
        key={"system-message"}
        autoHideDuration={2000}
      >
        <Alert severity={alert.variant}>
          {alert.msg}
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            sx={{ ml: 2 }}
            onClick={closeAlertMessage}
          >
            <Close fontSize="small" />
          </IconButton>
        </Alert>
      </Snackbar>

      {addAdminModal ? (
        <AddAdminModal
          title="Add New User"
          companyList={companyList}
          open={addAdminModal}
          setOpen={setAddAdminModal}
          setAlert={setAlert}
          getList={getFunction()}
        />
      ) : null}

      <DeleteConfirmationDialog
        open={deleteConfirmationDialog}
        onClose={handleDeleteConfirmationClose}
        onDelete={() => {
          handleDeleteConfirmationClose();
          handleDeleteAdmin(deleteProfile._id);
        }}
        name={`user ${deleteProfile.name}`}
      />

      {editUserModal ? (
        <EditModal
          companyList={companyList}
          data={userData}
          setData={setUserData}
          open={editUserModal}
          setOpen={setEditUserModal}
          alert={alert}
          setAlert={setAlert}
          getList={getFunction()}
        />
      ) : null}
    </div>
  );
};

export default UserList;
